import { useState } from "react";
import Button from "../common/Button";
import Input from "../common/Input";
import CustomSelect from "../common/CustomSelect";
import { createJob } from "../../../services/jobsServices";
import { toast } from "react-hot-toast";
import RichTextEditor from "../common/RichTextEditor";
import { useNavigate } from "react-router";

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "closed", label: "Closed" },
];

const AddJob = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [experience, setExperience] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [status, setStatus] = useState(statusOptions[0]);
  const [loading, setLoading] = useState(false);

  // Error tracking
  const [errors, setErrors] = useState({});

  // Clear error when user types/changes
  const handleChange = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));

    switch (field) {
      case "title":
        setTitle(value);
        break;
      case "experience":
        setExperience(value);
        break;
      case "location":
        setLocation(value);
        break;
      case "jobDescription":
        setJobDescription(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "status":
        setStatus(value);
        break;
      default:
        break;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title) newErrors.title = "Title is required";
    if (!experience) newErrors.experience = "Experience is required";
    if (!location) newErrors.location = "Location is required";
    if (!jobDescription)
      newErrors.jobDescription = "Job description is required";
    if (!description)
      newErrors.description = "Detailed description is required";
    if (!status) newErrors.status = "Status is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      title,
      experience,
      location,
      description,
      jobDescription,
      status: status?.value || "active",
    };

    try {
      setLoading(true);
      await createJob(payload);
      toast.success("Job created successfully!");

      // Reset form
      setTitle("");
      setExperience("");
      setLocation("");
      setDescription("");
      setJobDescription("");
      setStatus(statusOptions[0]); // reset to Active
      setErrors({});

      window.location.href = "/admin/jobs/list";
    } catch (error) {
      toast.error(error.message || "Failed to create job");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F5F0FF] p-6 rounded-lg font-sans space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Input
            label="Title"
            value={title}
            onChange={(val) => handleChange("title", val)}
            required
          />
          {errors.title && (
            <p className="text-red font-semibold text-sm">{errors.title}</p>
          )}
        </div>

        <div>
          <Input
            label="Experience"
            value={experience}
            onChange={(val) => handleChange("experience", val)}
            required
          />
          {errors.experience && (
            <p className="text-red font-semibold text-sm">
              {errors.experience}
            </p>
          )}
        </div>

        <div>
          <Input
            label="Location"
            value={location}
            onChange={(val) => handleChange("location", val)}
            required
          />
          {errors.location && (
            <p className="text-red font-semibold text-sm">{errors.location}</p>
          )}
        </div>

        <div className="col-span-3">
          <span className="block text-base font-semibold text-black mb-1">
            Job Description
          </span>
          <textarea
            className="w-full border border-gray-300 rounded-md p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent transition-all"
            value={jobDescription}
            onChange={(e) => handleChange("jobDescription", e.target.value)}
            rows={4}
            maxLength={400}
          />
          {errors.jobDescription && (
            <p className="text-red font-semibold text-sm">
              {errors.jobDescription}
            </p>
          )}
        </div>
      </div>

      <div>
        <RichTextEditor
          description={description}
          setDescription={(val) => handleChange("description", val)}
        />
        {errors.description && (
          <p className="text-red font-semibold text-sm">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 w-1/2 md:grid-cols-2 gap-4">
        <div>
          <CustomSelect
            label="Status"
            value={status}
            onChange={(val) => handleChange("status", val)}
            options={statusOptions}
            placeholder="Select"
          />
          {errors.status && (
            <p className="text-red font-semibold text-sm">{errors.status}</p>
          )}
        </div>
      </div>

      <div className="pt-4 space-y-2">
        <Button className="w-full" onClick={handleSubmit} disabled={loading}>
          {loading ? "Submitting..." : "Confirm & Add Jobs"}
        </Button>
        <Button
          onClick={() => navigate("/admin/jobs/list")}
          className="w-full"
          variant="text"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default AddJob;
